"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { Calendar, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Clock, Mail, Phone, MapPin, UserCheck, Sparkles, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
const trustPoints = [{
  title: "100% Direct Consultant Access",
  description: "You work directly with me — Yassine. No junior account managers or agency middle-men."
}, {
  title: "Honest, Data-Led SEO Strategy",
  description: "Real growth insights and sustainable search tactics tailored specifically to your market."
}, {
  title: "No Strings Attached",
  description: "A free 30-minute discovery call to evaluate your technical setup and organic growth opportunities."
}];
function InnerConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center shadow-md">
        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-accent/20 text-accent">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="font-serif text-2xl font-bold text-foreground">Consultation Requested</h3>
        <p className="mt-2 text-sm text-muted-foreground">{successMessage}</p>
        <p className="mt-4 text-xs text-muted-foreground">
          {"I'll personally review your site and get back to you within 24 business hours."}
        </p>
        <div className="mt-6">
          <Button asChild variant="outline" className="w-full">
            <Link to="/ContactPage">View Full Contact Info</Link>
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="fullName" rules={{
      required: "Your name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Your Full Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Work Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@company.com" className="bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="websiteUrl" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Website URL</FormLabel>
            <FormControl>
              <Input type="text" placeholder="https://yourcompany.com" className="bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent py-6 text-base font-semibold text-accent-foreground shadow-md transition-all hover:bg-accent/90">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Scheduling Your Call...
          </> : <>
            <SafeHydrate><Calendar className="mr-2 size-5" /></SafeHydrate>
            Book a Free SEO Consultation
          </>}
      </Button>

      <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
        <ShieldCheck className="size-4 text-primary" />
        <span>No spam. 100% confidential analysis directly by Yassine.</span>
      </div>
    </Form>;
}
export default function Consultation() {
  return <section id="consultationcta" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28">
      {}
      <div className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          {}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-muted px-4 py-1.5 text-xs font-medium text-foreground">
              <Sparkles className="size-3.5 text-accent" />
              <span>Direct Freelance SEO Expertise</span>
            </div>

            <h2 className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
              Ready to Accelerate Your Organic Search Growth?
            </h2>

            <p className="mt-6 text-base text-muted-foreground sm:text-lg">
              {"When you book a consultation with me, you speak directly with me — Yassine Essaadi. I don't hide behind account managers or automated report generators. I inspect your technical foundation, evaluate your keyword positioning, and map out a practical path to search visibility."}
            </p>

            <div className="mt-8 space-y-4">
              {trustPoints.map((point, index) => <div data-index={index} key={index} className="flex gap-3">
                  <div className="mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <UserCheck className="size-4" />
                  </div>
                  <div>
                    <h3 className="font-serif text-sm font-semibold text-foreground">{point.title}</h3>
                    <p className="text-xs text-muted-foreground">{point.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-border pt-8">
              <Button asChild size="lg" variant="outline" className="border-border">
                <Link to="/ContactPage">
                  <span>Get a Free Local SEO Audit</span>
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="size-4 text-accent" />
                <span>Typical response time: under 2 hours</span>
              </div>
            </div>

            {}
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div data-index={0} className="rounded-lg border border-border/60 bg-muted/40 p-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                  <Mail className="size-3.5 text-primary" />
                  <span>Email Me Directly</span>
                </div>
                <a href="mailto:contact@ye-consultancy.com" className="mt-1 block truncate text-xs text-muted-foreground hover:text-primary">
                  contact@ye-consultancy.com
                </a>
              </div>

              <div data-index={1} className="rounded-lg border border-border/60 bg-muted/40 p-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                  <Phone className="size-3.5 text-primary" />
                  <span>Phone Consultation</span>
                </div>
                <a href="tel:+447490984430" className="mt-1 block truncate text-xs text-muted-foreground hover:text-primary">
                  +44 7490984430
                </a>
              </div>

              <div data-index={2} className="rounded-lg border border-border/60 bg-muted/40 p-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                  <MapPin className="size-3.5 text-primary" />
                  <span>Office Location</span>
                </div>
                <Link to="/ContactPage" className="mt-1 block truncate text-xs text-muted-foreground hover:text-primary">
                  Windmill Hill Business Park, Swindon
                </Link>
              </div>
            </div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-5">
            <Card className="border-border/80 shadow-xl">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-serif text-xl font-bold text-foreground">
                  Book a Free SEO Consultation
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Fill in your details below for a direct, tailored review of your search performance.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
                fullName: "",
                email: "",
                websiteUrl: ""
              }} validationRules={{
                fullName: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                websiteUrl: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you! Your request has been sent directly to Yassine.">
                  <InnerConsultationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}